import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconChevronLeft } from "@/components/_icons/IconChevronLeft";
import { IconChevronRight } from "@/components/_icons/IconChevronRight";
import { useDate } from "@/hooks/useDate";
import { useDatePicker } from "@rehookify/datepicker";

import { Button } from "./components/button";
import { getYearsClassName } from "./components/classnames-utils";
import { Section } from "./components/section";

export interface YearPickerProps {
  onValueChange?: (year: number | null) => void;
  defaultValue?: number;
  minYear?: number;
  maxYear?: number;
}

// Currently only built for selecting a single date
export const YearPicker: FC<YearPickerProps> = ({ onValueChange, defaultValue, minYear, maxYear }) => {
  const { t, i18n } = useTranslation("common");
  const { TODAY } = useDate();
  const [selectedDateInternal, setSelectedDateInternal] = useState<Date | null>(
    defaultValue ? new Date(defaultValue, 0, 1) : null,
  );
  // the offsetDate is the that the calendar displays, not neccessary the selected date.
  const [offsetDate, setOffsetDate] = useState<Date>(defaultValue ? new Date(defaultValue, 0, 1) : TODAY);

  const selectedDates = useMemo(() => {
    if (!selectedDateInternal) {
      return [];
    }
    return [selectedDateInternal];
  }, [selectedDateInternal]);

  const handleYearSelected = (year: number) => {
    const dateFromYear = new Date(year, 0, 1);
    setSelectedDateInternal(dateFromYear);
    setOffsetDate(dateFromYear);
    onValueChange?.(year);
  };

  const handleDateChange = () => {
    // callback needs to be set;
    // however we handle the selected Date state internally
  };

  const {
    data: { years },
    propGetters: { nextYearsButton, previousYearsButton, yearButton },
  } = useDatePicker({
    selectedDates,
    offsetDate: offsetDate ?? undefined,
    onDatesChange: handleDateChange,
    calendar: {
      startDay: 1,
    },
    locale: {
      locale: i18n.language,
    },
    dates: {
      mode: "single",
      minDate: minYear ? new Date(minYear, 0, 1) : undefined,
      maxDate: maxYear ? new Date(maxYear, 0, 1) : undefined,
    },
  });

  return (
    <div className="shadow-xs shadow-sm block rounded-sm border border-slate-300 p-4 shadow-slate-300">
      {/* <h1 className="text-md w-full text-center mb-6">{formattedDates[0]}</h1> */}
      <div className="flex flex-row">
        <Section>
          <div className="mx-2 grid grid-cols-3 items-center gap-x-2 gap-y-2">
            {years.map((y) => (
              <Button
                key={y.$date.toString()}
                className={getYearsClassName("text-xs", y)}
                {...yearButton(y)}
                onClick={() => {
                  handleYearSelected(y.$date.getFullYear());
                }}
              >
                {y.year}
              </Button>
            ))}
            <div
              className="absolute bottom-0 left-0 top-0 flex flex-col justify-center pl-2 pt-2"
              {...previousYearsButton()}
            >
              <Button className="w-4" {...previousYearsButton()} aria-label={t("datepicker.previousYears")}>
                <IconChevronLeft className="h-3 w-3" aria-hidden="true" />
              </Button>
            </div>
            <div
              className="absolute bottom-0 right-0 top-0 flex flex-col justify-center pr-2 pt-2"
              {...nextYearsButton()}
            >
              <Button className="w-4" {...nextYearsButton()} aria-label={t("datepicker.nextYears")}>
                <IconChevronRight className="h-3 w-3" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};
